/**
 * Content-publishing-rule TARGET contract — the enriched shape the rules
 * endpoint returns, as distinct from `ContentTargetSchema` (the shape STORED on
 * `ContentPublishingRule.targets`).
 *
 * GET /v1/schools/:school_public_id/content-publishing-rules spreads the stored
 * target and layers read-time enrichment on top: a display `name`, and — for
 * `{type:'list'}` targets — the derived catalog health that tells an admin a
 * rule is pointed at a list Blackbaud no longer returns.
 *
 * Health is DERIVED on every read and never written back into the stored rule
 * JSON (KTD6): missing-ness is non-terminal, so a cached verdict would go stale
 * the moment the list reappears.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-02-003-fix-blackbaud-list-targeting-reliability-plan.md (U7)
 */

import { z } from 'zod'
import { ListCatalogStatusSchema } from './advanced-lists'

/**
 * Health of one `{type:'list'}` rule target. A superset of the catalog's own
 * `ListCatalogStatus`, because a *target* can fail in a way a catalog row
 * cannot: the id may resolve to no `z_RefLists` row for this school at all.
 *
 *   - `live`       — the list is in this school's catalog and looks healthy.
 *   - `missing`    — the list is in this school's catalog but repeated COMPLETE
 *                    fetches have not returned it (see `lastSeenInCatalogAt`
 *                    for whether it was EVER observed).
 *   - `unresolved` — no `z_RefLists` row for this school matches the `listId`
 *                    at all: an id from another tenant, a hard-deleted mirror
 *                    row, or a client bug. Distinct from `missing` because the
 *                    remediation differs and because there is no name to show —
 *                    this is the case that used to render a raw cuid.
 *
 * Three states, not two, and none of them says "deleted": Blackbaud reports a
 * deleted list, an unshared list and a permission-lost list identically, and
 * their remediations differ.
 */
export const ListTargetHealthSchema = z.enum(['live', 'missing', 'unresolved'])

export type ListTargetHealth = z.infer<typeof ListTargetHealthSchema>

/**
 * A `{type:'list'}` target as returned by the rules endpoint.
 *
 * `listId` (never a bare `id`) is the list's school-scoped `z_RefLists.publicId`
 * — KTD4. `name` is absent when the id did not resolve, which is exactly the
 * `unresolved` case.
 */
export const PublishingRuleListTargetSchema = z.object({
  type: z.literal('list'),
  listId: z.string(),
  isPrimary: z.boolean().optional(),
  /** Display name from `z_RefLists`; absent when `catalogStatus` is `unresolved`. */
  name: z.string().nullable().optional(),
  /** Derived per read; never persisted (KTD6). */
  catalogStatus: ListTargetHealthSchema,
  /**
   * ISO timestamp of the last real sighting in a Blackbaud catalog fetch, or
   * **null when this list has never once been observed** (and always null when
   * `unresolved`).
   *
   * Never substitute the mirror row's `createdAt` here. The backend derivation
   * anchors its time floor on `createdAt` when there is no sighting, but
   * surfacing that date as "last seen" would claim an observation that never
   * happened. Null needs its own copy ("Never confirmed in Blackbaud").
   */
  lastSeenInCatalogAt: z.string().nullable()
})

export type PublishingRuleListTarget = z.infer<typeof PublishingRuleListTargetSchema>

/** The catalog statuses that mean "this target will not reach anybody today". */
export const UNHEALTHY_LIST_TARGET_STATUSES: readonly ListTargetHealth[] = ['missing', 'unresolved']

/** Narrow an enriched rule target to the list arm. */
export function isPublishingRuleListTarget(target: { type?: string }): target is PublishingRuleListTarget {
  return target?.type === 'list'
}
